import { Controller, useFormContext } from 'react-hook-form';
import type { BaseSyntheticEvent, ReactElement } from 'react';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ButtonMain,
  Radio,
  Selection,
} from '@quero-delivery/quero-components-web';
import { Button } from '@querodelivery/uni-react';
import Tooltip from 'rc-tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle } from '@fortawesome/pro-solid-svg-icons';
import keys from 'lodash/keys';
import { isEmpty } from 'lodash';
import * as Form from '@radix-ui/react-form';
import styles from './fare-form.module.scss';
import type {
  AddressManipulationProps,
  District,
  FareType,
  GroupedDistricts,
  TypeFranchiseForm,
} from '../../types';
import { FARE_OF_TYPE, FareTabItems } from '../../types';
import {
  faresMoneyInputFormat,
  convertMoneyInputFormatToFloatWithNegative,
} from '@/Utils/FnUtils';
import { queryClient } from '@/_config/lib/react-query/query-client';
import type { PaymentMethodsTypesThatNeedReturnTypes } from '@/Interfaces/Data';
import { dialog } from '@/infra/dialog/Dialog';
import useUf from '@/hooks/address/useUf';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import { Checkbox } from '@/_components/checkbox';
import { useGetPlaces } from '@/_features/places/api/get-places';
import useScreenType from '@/hooks/util/useScreenType';
import { paymentMethodsThatNeedReturn } from '@/static/FranchiseConstants';
import { GET_PLACE_FARE, useGetPlaceFare } from '../../api/get-place-fare';
import { useSharePlaceFare } from '../../api/post-share-place-fare';
import { ImportFareConfirmModal } from '../import-fare-confirm-modal/import-fare-confirm-modal';
import { ImportFareSelectModal } from '../import-fare-select-modal/import-fare-select-modal';
import { FareTab } from '../fare-tab/fare-tab';
import { FareMap } from '../fare-map/fare-map';
import { FareDistrictsList } from '../fare-districts-list/fare-districts-list';
import { FareDistrictsAccordion } from '../fare-districts-accordion/fare-districts-accordion';
import { colors } from '@/Themes/Colors';
import type { CityFareFormAccessMode } from '../../hooks/use-city-fare-form-access';

interface FareFormProps {
  isEditing?: boolean;
  handleSubmit: (e?: BaseSyntheticEvent) => Promise<void>;
  fareType: FareType;
  isSubmitting?: boolean;
  addressManipulationProps?: AddressManipulationProps;
  importPlacesCityId?: string;
  accessMode?: CityFareFormAccessMode;
}

export const FareForm = ({
  isEditing,
  fareType,
  handleSubmit,
  isSubmitting: loadingSubmitting,
  addressManipulationProps,
  importPlacesCityId,
  accessMode = 'full',
}: FareFormProps) => {
  const navigate = useNavigate();
  const isMobile = useScreenType('mobile');
  const [isImportSelectModalOpen, setIsImportSelectModalOpen] = useState(false);
  const [isImportConfirmModalOpen, setIsImportConfirmModalOpen] =
    useState(false);
  const [selectedImportPlaceId, setSelectedImportPlaceId] = useState('');

  const { addressLocation, possibleLocal } = addressManipulationProps || {};

  const { ufOptions } = useUf();

  const isGeneralFieldsDisabled = accessMode !== 'full';
  const isReadonly = accessMode === 'readonly';

  const methods = useFormContext<TypeFranchiseForm>();
  const {
    control,
    formState: { errors, isSubmitting, isDirty },
    setValue,
    watch,
  } = methods;

  const bairros: District[] = watch('bairros');
  const fareOfType = watch('fareOfType');
  const currentPlaceId = watch('placeId');

  const startsOnTheTab =
    fareType === 'Place' || fareOfType === FARE_OF_TYPE.NEIGHBORHOOD
      ? FareTabItems.NEIGHBORHOOD
      : FareTabItems.MAP;

  const isPlaceFare = fareType === 'Place';
  const { mutate: sharePlaceFare, isLoading: isImportingFare } =
    useSharePlaceFare();

  const { data: importablePlaceFares } = useGetPlaceFare(
    { placeId: selectedImportPlaceId || '' },
    { enabled: !!selectedImportPlaceId },
  );

  const STALE_TIME_ONE_HOUR_FOR_PLACES_TO_IMPORT = 1000 * 60 * 60 * 24;

  const { data: placesResponse, isFetching: isLoadingPlacesToImport } =
    useGetPlaces(
      {
        cityId: importPlacesCityId || '',
        limit: 1000,
        hasFare: true,
      },
      {
        enabled: !!importPlacesCityId && isImportSelectModalOpen,
        staleTime: STALE_TIME_ONE_HOUR_FOR_PLACES_TO_IMPORT,
      },
    );

  useEffect(() => {
    if (isGeneralFieldsDisabled || !addressLocation) {
      return;
    }

    setValue('localizacao.latitude', addressLocation.latitude, {
      shouldDirty: true,
    });
    setValue('localizacao.longitude', addressLocation.longitude, {
      shouldDirty: true,
    });
  }, [addressLocation, isGeneralFieldsDisabled]);

  async function handleCancelForm() {
    const confirmed = await dialog.confirm(
      <div className={styles.cancelContainer}>
        <p>
          {`Tem certeza que deseja cancelar a \n${
            isEditing ? 'alteração' : 'criação'
          } da tarifa?`}
        </p>
      </div>,

      { isAlert: true },
    );

    if (!confirmed) {
      return;
    }

    navigate(-1);
  }

  function renderSuggestion(suggestion: string) {
    return <small>Sugestão: {suggestion}</small>;
  }

  const renderInputLabel = (label: string, overlay: string) => {
    return (
      <div className={styles.inputLabelWithTooltip}>
        <label>{label}</label>
        <Tooltip
          overlay={overlay}
          overlayStyle={isMobile ? { width: '50%' } : { width: '20%' }}
          placement="top"
        >
          <FontAwesomeIcon icon={faQuestionCircle} />
        </Tooltip>
      </div>
    );
  };

  function groupDistrictsByCity(districts: District[]): GroupedDistricts[] {
    const grouped: Record<string, GroupedDistricts> = {};

    districts.forEach((district, index) => {
      if (district.generalCityId && !grouped[district.generalCityId]) {
        grouped[district.generalCityId] = {
          cityName: district.cityName,
          cityId: district.cityId,
          bairros: [],
        };
      }
      if (district.generalCityId) {
        grouped[district.generalCityId].bairros.push({ ...district, index });
      }
    });

    return Object.values(grouped);
  }

  const groupedDistricts = groupDistrictsByCity(bairros || []);
  const hasSharedZones = groupedDistricts.length > 1;
  const importablePlaceOptions = useMemo(
    () =>
      (placesResponse?.data || [])
        .filter(place => place._id !== currentPlaceId)
        .map(place => ({
          key: place._id,
          text: place.nomeExibicao,
          value: place._id,
        })),
    [currentPlaceId, placesResponse],
  );

  const tabMenus: Record<FareTabItems, ReactElement> = {
    [FareTabItems.MAP]: (
      <FareMap addressManipulationProps={addressManipulationProps} />
    ),
    [FareTabItems.NEIGHBORHOOD]: hasSharedZones ? (
      <>
        {groupedDistricts.map(group => {
          return (
            <FareDistrictsAccordion
              fareType={fareType}
              districts={group.bairros}
              key={group.cityId}
              cityName={group.cityName}
              cityId={group.cityId}
            />
          );
        })}
      </>
    ) : (
      <FareDistrictsList
        fareType={fareType}
        districts={bairros}
        isOnAccordion={false}
      />
    ),
  };

  function handleCloseImportFlow() {
    setIsImportSelectModalOpen(false);
    setIsImportConfirmModalOpen(false);
    setSelectedImportPlaceId('');
  }

  function handleOpenImportFlow() {
    setSelectedImportPlaceId('');
    setIsImportConfirmModalOpen(false);
    setIsImportSelectModalOpen(true);
  }

  function handleBackToImportSelection() {
    setIsImportConfirmModalOpen(false);
    setIsImportSelectModalOpen(true);
  }

  function handleContinueImportFlow() {
    setIsImportSelectModalOpen(false);
    setIsImportConfirmModalOpen(true);
  }

  function handleImportFare() {
    if (!selectedImportPlaceId || !currentPlaceId) {
      return;
    }

    sharePlaceFare(
      {
        placeId: selectedImportPlaceId,
        placeIds: [currentPlaceId],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries([
            GET_PLACE_FARE,
            { placeId: currentPlaceId },
          ]);
          handleCloseImportFlow();
        },
      },
    );
  }

  return (
    <div className={styles.container}>
      <Form.Root className={styles.formContainer} onSubmit={handleSubmit}>
        <div className={styles.cityAndUfContainer}>
          {!isPlaceFare ? (
            <>
              <div className={styles.dropdownContainer}>
                <Controller
                  control={control}
                  name="nome"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <>
                        <TextInput
                          name="nome"
                          value={value}
                          onChange={onChange}
                          label="Cidade"
                          required
                          disabled={isGeneralFieldsDisabled}
                          errorMessage={errors.nome?.message}
                          error={!!errors.nome?.message}
                        />
                        {possibleLocal?.city && !value
                          ? renderSuggestion(possibleLocal.city)
                          : null}
                      </>
                    );
                  }}
                />
              </div>

              <div className={styles.dropdownContainer}>
                <Controller
                  control={control}
                  name="uf"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <>
                        <DropdownInput
                          search
                          label="UF"
                          options={ufOptions}
                          value={value}
                          required
                          onChange={selectedValue => {
                            if (typeof selectedValue === 'string') {
                              onChange(selectedValue);
                            }
                          }}
                          error={!!errors.uf?.message}
                          errorMessage={errors.uf?.message}
                          disabled={isGeneralFieldsDisabled}
                        />
                        {possibleLocal?.uf && !value
                          ? renderSuggestion(possibleLocal.uf)
                          : null}
                      </>
                    );
                  }}
                />
              </div>
            </>
          ) : (
            <>
              <div className={styles.dropdownContainer}>
                <Controller
                  control={control}
                  name="cityName"
                  render={({ field: { value } }) => {
                    return (
                      <TextInput
                        name="cityName"
                        label="Cidade"
                        value={value}
                        disabled
                      />
                    );
                  }}
                />
              </div>

              <div className={styles.dropdownContainer}>
                <Controller
                  control={control}
                  name="ufInitials"
                  render={({ field: { onChange, value } }) => {
                    return (
                      <DropdownInput
                        label="Estado"
                        search
                        clearable
                        value={value}
                        options={ufOptions}
                        onChange={selectedValue => {
                          if (typeof selectedValue === 'string') {
                            onChange(selectedValue);
                          }
                        }}
                        errorMessage={errors.ufInitials?.message}
                        disabled
                      />
                    );
                  }}
                />
              </div>

              <div className={styles.dropdownContainer}>
                <Controller
                  control={control}
                  name="establishmentName"
                  render={({ field: { value = '' } }) => {
                    return (
                      <TextInput
                        name="establishmentName"
                        label="Estabelecimento"
                        value={value}
                        disabled
                      />
                    );
                  }}
                />
              </div>
            </>
          )}

          {isPlaceFare && !isGeneralFieldsDisabled && (
            <div className={styles.importAction}>
              <Button size="lg" type="button" onClick={handleOpenImportFlow}>
                Importar tarifa
              </Button>
            </div>
          )}
        </div>

        <div className={styles.fareOfTypeContainer}>
          <span>Tarifa por: </span>
          <Controller
            control={control}
            name="fareOfType"
            render={({ field: { onChange, value } }) => {
              return (
                <div className={styles.fareOfTypeRadiosContainer}>
                  <Radio
                    onChange={() => {
                      if (isGeneralFieldsDisabled) {
                        return;
                      }
                      onChange(FARE_OF_TYPE.NEIGHBORHOOD);
                    }}
                    label="Bairro"
                    value={FARE_OF_TYPE.NEIGHBORHOOD}
                    checked={value === FARE_OF_TYPE.NEIGHBORHOOD}
                    className={styles.radio}
                    color={colors.brand10}
                    disabled={isGeneralFieldsDisabled}
                  />
                  <Radio
                    onChange={() => {
                      if (isGeneralFieldsDisabled) {
                        return;
                      }
                      onChange(FARE_OF_TYPE.KM);
                    }}
                    label="Km"
                    value={FARE_OF_TYPE.KM}
                    checked={value === FARE_OF_TYPE.KM}
                    color={colors.brand10}
                    disabled={isGeneralFieldsDisabled}
                  />
                </div>
              );
            }}
          />
        </div>

        <div className={styles.kmConfigContainer}>
          <div className={styles.kmConfigItem}>
            <span>Entrega base</span>
            <div className={styles.kmConfigInputs}>
              <Controller
                control={control}
                name="entregaBaseKm"
                render={({ field: { onChange, value } }) => {
                  return (
                    <TextInput
                      name="entregaBaseKm"
                      containerClassName={styles.paramInput}
                      label="Distância da entrega"
                      tag="Km"
                      value={value}
                      disabled={isGeneralFieldsDisabled}
                      onChange={e =>
                        onChange(faresMoneyInputFormat(e.target.value))
                      }
                      error={!!errors.entregaBaseKm?.message}
                      errorMessage={errors.entregaBaseKm?.message}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="valorEntregaBase"
                render={({ field: { onChange, value } }) => {
                  return (
                    <TextInput
                      name="valorEntregaBase"
                      containerClassName={styles.paramInput}
                      label="Valor"
                      tag="R$"
                      value={value}
                      disabled={isGeneralFieldsDisabled}
                      onChange={e =>
                        onChange(faresMoneyInputFormat(e.target.value))
                      }
                      error={!!errors.valorEntregaBase?.message}
                      errorMessage={errors.valorEntregaBase?.message}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="taxaEntregaBase"
                render={({ field: { onChange, value } }) => {
                  return (
                    <TextInput
                      name="taxaEntregaBase"
                      containerClassName={styles.paramInput}
                      label="Taxa do log"
                      tag="R$"
                      value={value}
                      disabled={isGeneralFieldsDisabled}
                      onChange={e =>
                        onChange(
                          convertMoneyInputFormatToFloatWithNegative(
                            e.target.value,
                          ),
                        )
                      }
                      error={!!errors.taxaEntregaBase?.message}
                      errorMessage={errors.taxaEntregaBase?.message}
                    />
                  );
                }}
              />
            </div>
          </div>
          <div className={styles.kmConfigItem}>
            <span>Km adicional</span>
            <div className={styles.kmConfigInputs}>
              <Controller
                control={control}
                name="valorKmAdicional"
                render={({ field: { onChange, value } }) => {
                  return (
                    <TextInput
                      name="valorKmAdicional"
                      containerClassName={styles.paramInput}
                      label="Valor por km adicional"
                      tag="R$"
                      value={value}
                      disabled={isGeneralFieldsDisabled}
                      onChange={e =>
                        onChange(faresMoneyInputFormat(e.target.value))
                      }
                      error={!!errors.valorKmAdicional?.message}
                      errorMessage={errors.valorKmAdicional?.message}
                    />
                  );
                }}
              />
              <Controller
                control={control}
                name="taxaEntregaKmAdicional"
                render={({ field: { onChange, value } }) => {
                  return (
                    <TextInput
                      name="taxaEntregaKmAdicional"
                      containerClassName={styles.paramInput}
                      label="Taxa por km adicional"
                      tag="R$"
                      value={value}
                      disabled={isGeneralFieldsDisabled}
                      onChange={e =>
                        onChange(faresMoneyInputFormat(e.target.value))
                      }
                      error={!!errors.taxaEntregaKmAdicional?.message}
                      errorMessage={errors.taxaEntregaKmAdicional?.message}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>

        <div className={styles.divider} />

        <div className={styles.otherParamsContainer}>
          <span className={styles.otherParamsTitle}>Outros parâmetros</span>
          <div className={styles.otherParamsContentContainer}>
            <div className={styles.otherParamsContent}>
              <span>Obrigatoriedade de Retorno</span>
              <div className={styles.otherParamsCheckboxes}>
                <Controller
                  control={control}
                  name="requiredReturnToConfirmPayMethods"
                  render={({ field: { onChange, value } }) => (
                    <>
                      {keys(paymentMethodsThatNeedReturn).map(payment => {
                        const isActive = value.includes(payment);
                        const hasError = !isEmpty(
                          errors.requiredReturnToConfirmPayMethods,
                        );
                        const checkboxLabel =
                          paymentMethodsThatNeedReturn[
                            payment as PaymentMethodsTypesThatNeedReturnTypes
                          ];
                        const handleChangeCheckbox = () => {
                          if (isReadonly) {
                            return;
                          }

                          const newPaymentTypes = isActive
                            ? value.filter(
                                paymentType => paymentType !== payment,
                              )
                            : [...value, payment];

                          onChange(newPaymentTypes);
                        };

                        return (
                          <Fragment key={payment}>
                            <Checkbox
                              id={payment}
                              key={payment}
                              checked={isActive}
                              label={checkboxLabel}
                              disabled={isReadonly}
                              onClick={handleChangeCheckbox}
                            />
                            {hasError &&
                            Array.isArray(
                              errors.requiredReturnToConfirmPayMethods,
                            ) ? (
                              <p className="text-error">
                                {errors.requiredReturnToConfirmPayMethods.join(
                                  ' - ',
                                )}
                              </p>
                            ) : null}
                          </Fragment>
                        );
                      })}
                    </>
                  )}
                />
              </div>
            </div>
            <div className={styles.otherParamsContent}>
              <span>Obrigatório entregador próximo ao estabelecimento </span>
              <Controller
                control={control}
                name="requiredNearPlaceToConfirm"
                render={({ field: { onChange, value } }) => (
                  <Selection
                    type="switch"
                    checked={value}
                    disabled={isGeneralFieldsDisabled}
                    onClick={() => {
                      if (isGeneralFieldsDisabled) {
                        return;
                      }
                      onChange(!value);
                    }}
                  />
                )}
              />
            </div>
          </div>
          <div className={styles.otherParamsInputsContainer}>
            <Controller
              control={control}
              name="maxDistanceDropsToGroup"
              render={({ field: { onChange, value } }) => {
                return (
                  <TextInput
                    name="maxDistanceDropsToGroup"
                    containerClassName={styles.paramInput}
                    label={renderInputLabel(
                      'Distância limite entre entregas',
                      'Valor máximo de quilometragem permitido entre endereços de entrega (cliente).',
                    )}
                    value={value}
                    tag="Km"
                    disabled={isReadonly}
                    onChange={e =>
                      onChange(faresMoneyInputFormat(e.target.value))
                    }
                    error={!!errors.maxDistanceDropsToGroup?.message}
                    errorMessage={errors.maxDistanceDropsToGroup?.message}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="raioMaxDistance"
              render={({ field: { onChange, value } }) => {
                return (
                  <TextInput
                    name="raioMaxDistance"
                    containerClassName={styles.paramInput}
                    label={renderInputLabel(
                      'Dist. entregador / estabelecimento',
                      'Valor máximo de quilometragem permitido entre o ponto de coleta (estabelecimento) e o entregador.',
                    )}
                    value={value}
                    tag="Km"
                    disabled={isReadonly}
                    onChange={e => {
                      onChange(faresMoneyInputFormat(e.target.value));
                    }}
                    error={!!errors.raioMaxDistance?.message}
                    errorMessage={errors.raioMaxDistance?.message}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="minDistanceCloseTo"
              render={({ field: { onChange, value } }) => {
                return (
                  <TextInput
                    name="minDistanceCloseTo"
                    containerClassName={styles.paramInput}
                    label={renderInputLabel(
                      'Dist. mínima do estabelecimento',
                      'A distância mínima evita que entregas sejam finalizadas sem deslocamento real, garantindo que o entregador esteja em rota ou no destino.',
                    )}
                    value={value}
                    tag="Km"
                    disabled={isReadonly}
                    onChange={e => {
                      onChange(faresMoneyInputFormat(e.target.value));
                    }}
                    error={!!errors.minDistanceCloseTo?.message}
                    errorMessage={errors.minDistanceCloseTo?.message}
                  />
                );
              }}
            />
            <Controller
              control={control}
              name="qtdMaxEntregaGroup"
              render={({ field: { onChange, value } }) => {
                return (
                  <TextInput
                    name="qtdMaxEntregaGroup"
                    containerClassName={styles.paramInput}
                    label={renderInputLabel(
                      'Quantidade de entregas',
                      'Total de entregas feitas em uma única corrida, permitindo que o entregador leve mais de um pedido por vez, otimizando o tempo e o percurso.',
                    )}
                    value={value}
                    onChange={onChange}
                    tag="Und"
                    disabled={isReadonly}
                  />
                );
              }}
            />
          </div>

          <div
            className={
              isGeneralFieldsDisabled ? styles.readOnlySection : undefined
            }
          >
            <FareTab
              startsOnTheTab={startsOnTheTab}
              tabMenus={tabMenus}
              fareType={fareType}
            />
          </div>

          {!isReadonly && isDirty && (
            <div className={styles.formAction}>
              <ButtonMain
                secondary
                type="button"
                onClick={handleCancelForm}
                disabled={isSubmitting || loadingSubmitting}
              >
                <span>cancelar</span>
              </ButtonMain>

              <ButtonMain
                type="submit"
                loading={isSubmitting || loadingSubmitting}
                disabled={isSubmitting || loadingSubmitting}
              >
                <b>{isEditing ? 'atualizar' : 'criar'}</b>
              </ButtonMain>
            </div>
          )}
        </div>
      </Form.Root>

      <ImportFareSelectModal
        open={isImportSelectModalOpen}
        onClose={handleCloseImportFlow}
        onConfirm={handleContinueImportFlow}
        options={importablePlaceOptions}
        selectedPlaceId={selectedImportPlaceId}
        onSelectionChange={value => {
          if (Array.isArray(value)) {
            return;
          }

          setSelectedImportPlaceId(value);
        }}
        loading={isLoadingPlacesToImport}
      />

      <ImportFareConfirmModal
        open={isImportConfirmModalOpen}
        onClose={handleCloseImportFlow}
        onCancel={handleBackToImportSelection}
        onConfirm={handleImportFare}
        placeFare={importablePlaceFares}
        loading={isImportingFare}
      />
    </div>
  );
};
